import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { encryptToUrl } from './utils/crypto';
import axiosInstance from './utils/axiosInstance';
import AuthService from './services/authService';
import { isLabAssistantStaff } from './utils/labRole';
import { resolveLandingPath } from './utils/workspaces';

const GlobalStyle = createGlobalStyle`
  :root {
    --bg: #f3f4f6;
    --card-bg: #ffffff;
    --primary: #1f2937; /* neutral-800 */
    --primary-hover: #111827; /* neutral-900 */
    --text: #111827; /* gray-900 */
    --muted: #6b7280; /* gray-500 */
    --border: #e5e7eb; /* gray-200 */
    --shadow: 0 18px 30px rgba(15, 23, 42, 0.08);
  }
  *, *::before, *::after { box-sizing: border-box; }
  html, body, #root { height: 100%; }
  body { margin: 0; background: var(--bg); font-family: system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, 'Noto Sans', 'Helvetica Neue', Arial, 'Apple Color Emoji', 'Segoe UI Emoji'; color: var(--text); }
`;

const Page = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  background: radial-gradient(circle at top left, #e5e7eb 0, #f3f4f6 45%, #f9fafb 100%);
`;

const Container = styled.div`
  max-width: 480px;
  width: 100%;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const LockIcon = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 12px;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: #ffffff;
  display: grid;
  place-items: center;
  color: var(--primary);
  font-weight: 600;
  font-size: 20px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const Version = styled.div`
  margin-top: 6px;
  color: var(--muted);
  font-weight: 600;
  font-size: 12px;
`;

const Subtitle = styled.p`
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 14px;
`;

const Card = styled.div`
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--shadow);
  padding: 28px 24px 24px;
  border: 1px solid var(--border);
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: var(--muted);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: 1px solid var(--border);
  padding: 0 12px;
  font-size: 14px;
  outline: none;
  background: #ffffff;
  transition: box-shadow 0.2s, border-color 0.2s, background 0.2s;
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(15, 23, 42, 0.18);
    background: #fff;
  }
`;

const Field = styled.div`
  & + & { margin-top: 16px; }
`;

const PasswordWrap = styled.div`
  position: relative;
`;

const ToggleBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  height: 32px;
  width: 32px;
  border: none;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  border-radius: 6px;
  transition: background 0.15s, color 0.15s;
  &:hover { background: #eef2ff; color: var(--primary); }
  &:focus { outline: 2px solid rgba(79, 70, 229, 0.35); outline-offset: 2px; }
`;

const RememberRow = styled.label`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--muted);
  cursor: pointer;
  user-select: none;
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: var(--primary);
  cursor: pointer;
`;

const SessionNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
  font-size: 13.5px;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const ErrorNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 20px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 13.5px;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const SignIn = styled.button`
  margin-top: 20px;
  width: 100%;
  height: 44px;
  border-radius: 8px;
  border: none;
  background: var(--primary);
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  transition: background 0.2s, transform 0.02s, box-shadow 0.2s;
  &:hover { background: var(--primary-hover); box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18); }
  &:active { transform: translateY(0.5px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const RedirectingPage = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--muted);
  font-size: 14px;
`;

const Footer = styled.div`
  margin-top: 12px;
  text-align: center;
  color: #9ca3af; /* gray-400 */
  font-size: 12px;
`;

// After a successful (or already-active) login, decide routing based on
// coordinate course count and staff category. Shared between the submit
// handler and the "already signed in" auto-redirect on mount, so both paths
// land in the same place.
async function decideRoute(superAdmin) {
  let nextPath = '/admin/dashboard';
  try {
    // One workspace -> open it directly; several -> let the user pick on /workspaces.
    const landing = await resolveLandingPath({ superAdmin });
    if (landing) return landing;
  } catch (_) {
    // fall through to the legacy routing below
  }
  try {
    const accessResp = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
    const accessData = accessResp?.data || {};

    const coordinateCourses = accessData.coordinateCourses || {};
    const accessibleCourseIds = Array.isArray(accessData.accessibleCourseIds)
      ? accessData.accessibleCourseIds
      : [];

    const coordinateCount = Object.keys(coordinateCourses).length || accessibleCourseIds.length;

    if (coordinateCount === 0) {
      const staff = superAdmin?.staff || {};
      const categoryRaw = staff.category || staff.staffType || staff.type || '';
      const category = String(categoryRaw).toLowerCase();
      const isAcademicCategory = category === 'academic';
      const isSalesCategory = category === 'sales';
      const roleRaw = staff.role || '';
      const roleLower = String(roleRaw).toLowerCase().replace(/\s+/g, '');
      const staffTypeLower = String(staff.staffType || '').toLowerCase().replace(/\s+/g, '');
      const isRecoveryOfficer = staffTypeLower === 'recoveryofficer' || roleLower === 'recoveryofficer' || category === 'recovery';

      if (isLabAssistantStaff(staff)) {
        // Lab Assistant is an "academic" role but has its own dashboard
        nextPath = '/lab/dashboard';
      } else if (isRecoveryOfficer) {
        // Recovery Officer: takes priority over category field
        nextPath = '/recovery/dashboard';
      } else if (isAcademicCategory) {
        // Academic staff without coordinate courses: lecturer routing only
        nextPath = '/lecturer/dashboard';
      } else if (isSalesCategory) {
        // Sales staff without coordinate courses: sales dashboard routing
        nextPath = '/sales/dashboard';
      } else {
        // Other staff without coordinate courses: default to admin dashboard
        nextPath = '/admin/dashboard';
      }
    } else {
      // Has one or more coordinate courses: allow both admin and lecturer; default to admin dashboard
      nextPath = '/admin/dashboard';
    }
  } catch (_) {
    // On failure, keep default admin dashboard
    nextPath = '/admin/dashboard';
  }
  return nextPath;
}

export default function Login() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  // Pre-fill from a previously "remembered" login, if any, on this device.
  const [remembered] = useState(() => AuthService.getRememberedCredentials());
  const [username, setUsername] = useState(() => remembered?.username || '');
  const [password, setPassword] = useState(() => remembered?.password || '');
  const [rememberMe, setRememberMe] = useState(() => !!remembered);
  const [submitting, setSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  // Consumed once on mount: true only when axiosInstance's 401 handler just
  // redirected here after the api key stopped working, never on a plain visit.
  const [sessionExpired] = useState(() => AuthService.consumeSessionExpiredFlag());
  // Stays true (never flips to false) while an already-authenticated visitor
  // is being routed straight into the app, so the login form never flashes.
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    const existing = AuthService.getUser();
    if (existing?.apiKey) {
      decideRoute(existing.superAdmin).then((nextPath) => navigate(nextPath, { replace: true }));
    } else {
      setCheckingSession(false);
    }
    // Runs once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setLoginError(null);
    axiosInstance
      .post(`/admin/login`, { username, password }, { headers: { 'Content-Type': 'application/json' } })
      .then((resp) => {
        const data = resp?.data;
        const payload = {
          message: data?.message,
          apiKey: data?.apiKey,
          superAdmin: data?.superAdmin || null,
          staffId: data?.superAdmin?.staff?.staffId || null,
        };
        const encryptedUrl = encryptToUrl(payload);
        const valueToStore = `kaarya://${encryptedUrl}`;
        try {
          window.localStorage.setItem('kaarya_user', valueToStore);
        } catch (_) {}

        if (rememberMe) {
          AuthService.rememberCredentials(username, password);
        } else {
          AuthService.forgetCredentials();
        }

        decideRoute(payload.superAdmin).then((nextPath) => navigate(nextPath, { replace: true }));
      })
      .catch((error) => {
        const msg = error?.response?.data?.message || error?.message || 'Login failed';
        setLoginError(msg);
        setSubmitting(false);
      });
  };

  if (checkingSession) {
    return (
      <>
        <GlobalStyle />
        <RedirectingPage>Signing you in…</RedirectingPage>
      </>
    );
  }

  return (
    <Page>
      <GlobalStyle />
      <Container>
        <Header>
          <LockIcon>KA</LockIcon>
          <Title>Kaaryaalaya</Title>
          <Version>V2.0</Version>
          <Subtitle>Secure staff portal sign-in</Subtitle>
        </Header>

        <Card>
          {sessionExpired && (
            <SessionNotice role="status">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M12 7.5v5.25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="16.25" r="1" fill="currentColor" />
              </svg>
              <span>Your session has expired for your security. Please sign in again to continue.</span>
            </SessionNotice>
          )}
          {loginError && (
            <ErrorNotice role="alert">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M12 7.5v5.25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <circle cx="12" cy="16.25" r="1" fill="currentColor" />
              </svg>
              <span>{loginError}</span>
            </ErrorNotice>
          )}
          <form onSubmit={onSubmit}>
            <Field>
              <Label>Username</Label>
              <Input
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => { setUsername(e.target.value); setLoginError(null); }}
                autoComplete="username"
              />
            </Field>

            <Field>
              <Label>Password</Label>
              <PasswordWrap>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setLoginError(null); }}
                  autoComplete="current-password"
                />
                <ToggleBtn
                  type="button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  title={showPassword ? 'Hide password' : 'Show password'}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? (
                    // Eye-off icon
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M2.46 12.74C3.9 9.3 7.33 6.5 12 6.5c1.76 0 3.4.39 4.85 1.08" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21.54 11.26C20.1 14.7 16.67 17.5 12 17.5c-1.76 0-3.4-.39-4.85-1.08" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  ) : (
                    // Eye icon
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <path d="M2.5 12c2.2-4.5 6.2-7.5 9.5-7.5s7.3 3 9.5 7.5c-2.2 4.5-6.2 7.5-9.5 7.5S4.7 16.5 2.5 12z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="3.5" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  )}
                </ToggleBtn>
              </PasswordWrap>
            </Field>

            <RememberRow>
              <Checkbox
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
              Remember my username and password on this device
            </RememberRow>

            <SignIn type="submit" disabled={submitting}>{submitting ? 'Signing in…' : 'Sign in'}</SignIn>
          </form>
        </Card>

        <Footer>© 2025 Kaaryaalaya. All rights reserved.</Footer>
      </Container>
    </Page>
  );
}
